import { ButtonBase } from '@mui/material';
import SupportingEvidence from 'components/Grading/SupportingEvidence';
import { useDialog } from 'contexts/dialog';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Divider,
    Dropdown,
    IconButton,
    Paper,
    RichTextArea,
    Stack,
    styled,
    Text,
    Tooltip,
    ViewMore,
} from 'shell/components';
import { getTemplatedFeedback, getTooltipComponent, scrollElementIntoView, stripHTML } from 'utils/helper';

const FeedbackRenderer = ({
    assignment,
    currentSubmission,
    selectedFileId,
    setSelectedFileId,
    locations,
    form,
    setForm,
    selectedFeedbackId,
    setSelectedFeedbackId,
    handleCriterionNavigation,
}) => {
    const { setDialog, closeDialog } = useDialog();
    const { joyrideRun } = useJoyride();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(form?.feedback?.commentary);

    const isFeedbackCustom = currentSubmission.ai.feedback.feedback_type === 'custom';
    const isFeedbackRubric = currentSubmission.ai.feedback.feedback_type === 'rubric';
    const isFeedbackTemplated = currentSubmission.ai.feedback.feedback_type === 'templated';

    const locationData = useMemo(() => {
        return {
            locations,
            files: currentSubmission?.files ?? [],
        };
    }, [locations, currentSubmission?.files]);

    useEffect(() => {
        if (selectedFeedbackId && form.feedback?.improvement_suggestions) {
            const suggestion = form.feedback.improvement_suggestions.find(({ id }) => id === selectedFeedbackId);
            if (suggestion) {
                const index = assignment.rubric.criteria.findIndex(({ id }) => id === suggestion.criterion_id);
                if (index !== -1) {
                    const timeoutId = setTimeout(() => {
                        scrollElementIntoView(`feedback-criterion-${index}`);
                    }, 100);
                    return () => clearTimeout(timeoutId);
                }
            }
        }
    }, [assignment.rubric.criteria, form.feedback?.improvement_suggestions, selectedFeedbackId]);

    const handleClickEdit = () => {
        setIsEditing(true);
        setValue(form.feedback.commentary ?? '');
    };

    const handleClickCopy = async (string) => {
        await navigator.clipboard.writeText(stripHTML(string));
        message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
    };

    const handleClickDelete = () => {
        setDialog({
            open: true,
            message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
            description: t(
                'dialog.delete-feedback.description',
                'Are you sure you want to delete this feedback? This can’t be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({ ...current, feedback: { ...current.feedback, commentary: '' } }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickSave = () => {
        setForm((current) => ({ ...current, feedback: { ...current.feedback, commentary: value } }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(form.feedback.commentary);
    };

    const handleClickCopyAll = async () => {
        const noFeedbackTranslationArgs = assignment?.api_assignment_id
            ? [
                  'feedback-section.no-feedback-no-pushback',
                  'No feedback provided. This message will not be passed back.',
              ]
            : ['feedback-section.no-feedback', 'No feedback provided.'];

        const str = form.feedback.improvement_suggestions
            .filter(({ active }) => active)
            .reduce((acc, cur, index) => {
                if (isFeedbackRubric) {
                    const { criterion_id, positive, negative } = cur;
                    let feedback = '';
                    if (index) feedback += '\n\n';
                    const criterion = assignment.rubric.criteria.find(({ id }) => id === criterion_id);
                    feedback += `${criterion.title}\n\n`;
                    feedback += `${t('feedback-section.positive-title', 'What you did well')}\n\n`;
                    feedback +=
                        !positive.active && positive.text
                            ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                            : positive.text === ''
                              ? t(...noFeedbackTranslationArgs)
                              : stripHTML(positive.text);
                    feedback += `\n\n${t('feedback-section.improvement-title', 'How you can improve')}\n\n`;
                    feedback +=
                        !negative.active && negative.text
                            ? t('feedback-section.deleted-feedback', 'This feedback was deleted.')
                            : negative.text === ''
                              ? t(...noFeedbackTranslationArgs)
                              : stripHTML(negative.text);
                    return `${acc}${feedback}`;
                }
                return !index ? `${acc}${stripHTML(cur.text)}` : `${acc}\n\n${stripHTML(cur.text)}`;
            }, '');
        await navigator.clipboard.writeText(str);
        message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
    };

    const handleClickDeleteAll = async () => {
        setDialog({
            open: true,
            message: t('dialog.delete-all-feedback.title', 'Delete All Feedback?'),
            description: t(
                'dialog.delete-all-feedback.description',
                'Are you sure you want to delete all feedback? This can’t be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({
                    ...current,
                    feedback: {
                        ...current.feedback,
                        improvement_suggestions: current.feedback.improvement_suggestions.map((item) =>
                            isFeedbackRubric
                                ? {
                                      ...item,
                                      positive: {
                                          ...item.positive,
                                          active: false,
                                      },
                                      negative: {
                                          ...item.negative,
                                          active: false,
                                      },
                                  }
                                : {
                                      ...item,
                                      active: false,
                                  }
                        ),
                    },
                }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    if (!form.feedback) return null;

    if (isFeedbackTemplated) {
        return (
            <>
                <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} direction={'row'} spacing={2}>
                        <Text variant={'p1'} weight={'semibold'}>
                            {t('feedback-section.assignment-feedback-label', 'Assignment Feedback')}
                        </Text>
                        <Tooltip
                            content={t(
                                'feedback-section.templated-feedback-tooltip',
                                'This assignment is using Smart Feedback Templates. When you make a grading selection, the feedback template for that grade will appear here.'
                            )}
                            type={'info'}
                        />
                    </Stack>
                    <Stack direction={'row'} spacing={2}>
                        <IconButton
                            aria-label={'copy feedback'}
                            disabled={!Object.keys(form.criteria).length}
                            kind={'copy'}
                            size={'small'}
                            tooltip={{ component: t('common:controls.copy', 'Copy') }}
                            variant={'square'}
                            onClick={() => handleClickCopy(getTemplatedFeedback(assignment, form))}
                        />
                    </Stack>
                </BackgroundStack>
                <Stack sx={{ flex: 1, position: 'relative', p: 3 }}>
                    {Object.keys(form.criteria).length ? (
                        <Text rich>{getTemplatedFeedback(assignment, form)}</Text>
                    ) : (
                        <CenterText align={'center'} color={'light'}>
                            {t(
                                'feedback-section.templated-feedback-description',
                                'This assignment is using Smart Feedback Templates. Feedback will appear here as you make grading selections.'
                            )}
                        </CenterText>
                    )}
                </Stack>
            </>
        );
    }

    return (
        <>
            <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                    <Text variant={'p1'} weight={'semibold'}>
                        {t('feedback-section.general-feedback-label', 'General Feedback')}
                    </Text>
                    {!isFeedbackRubric && (
                        <Tooltip
                            content={getTooltipComponent(
                                t('feedback-section.general-feedback-label', 'General Feedback'),
                                t(
                                    'feedback-section.system-feedback-tooltip',
                                    'This section provides a broad overview, highlighting key strengths and areas for improvement across student work.'
                                )
                            )}
                            type={'info'}
                        />
                    )}
                </Stack>
                <Stack direction={'row'} spacing={2}>
                    <IconButton
                        aria-label={!form.feedback.commentary ? 'add feedback' : 'edit feedback'}
                        disabled={joyrideRun}
                        kind={!form.feedback.commentary ? 'add' : 'edit'}
                        size={'small'}
                        tooltip={{
                            component: !form.feedback.commentary
                                ? t('common:controls.add', 'Add')
                                : t('common:controls.edit', 'Edit'),
                        }}
                        variant={'square'}
                        onClick={handleClickEdit}
                    />
                    <IconButton
                        aria-label={'copy feedback'}
                        disabled={!form.feedback.commentary}
                        kind={'copy'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.copy', 'Copy') }}
                        variant={'square'}
                        onClick={() => handleClickCopy(form.feedback.commentary)}
                    />
                    <IconButton
                        aria-label={'delete feedback'}
                        disabled={!form.feedback.commentary || joyrideRun}
                        kind={'delete'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.delete', 'Delete') }}
                        variant={'square'}
                        onClick={handleClickDelete}
                    />
                </Stack>
            </BackgroundStack>
            {isEditing ? (
                <Stack sx={{ p: 3 }} spacing={3}>
                    <RichTextArea
                        placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                        value={value}
                        onChange={setValue}
                    />
                    <Stack direction={'row-reverse'} spacing={3}>
                        <Button onClick={handleClickSave}>{t('common:controls.save', 'Save')}</Button>
                        <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                            {t('common:controls.cancel', 'Cancel')}
                        </Button>
                    </Stack>
                </Stack>
            ) : !form.feedback.commentary ? (
                <Divider />
            ) : (
                <Stack sx={{ p: 3 }}>
                    <Text rich>{form.feedback.commentary}</Text>
                </Stack>
            )}
            {!isFeedbackCustom && (
                <>
                    <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Stack alignItems={'center'} direction={'row'} spacing={2}>
                            <Text variant={'p1'} weight={'semibold'}>
                                {currentSubmission.ai.feedback.feedback_type === 'rubric'
                                    ? t('feedback-section.assignment-feedback-label', 'Assignment Feedback')
                                    : t('feedback-section.detailed-feedback-label', 'Detailed Feedback')}
                            </Text>
                            {!isFeedbackRubric && (
                                <Tooltip
                                    content={getTooltipComponent(
                                        t('feedback-section.detailed-feedback-label', 'Detailed Feedback'),
                                        t(
                                            'feedback-section.detailed-feedback-tooltip',
                                            'Here, specific feedback is provided with highlighted examples. Students can use these examples to help them identify other areas where they can improve.'
                                        )
                                    )}
                                    type={'info'}
                                />
                            )}
                        </Stack>
                        <Stack direction={'row'} spacing={2}>
                            <IconButton
                                aria-label={'copy feedback'}
                                disabled={
                                    isFeedbackRubric
                                        ? form.feedback.improvement_suggestions.every(
                                              ({ positive, negative }) => !positive.action && !negative.active
                                          )
                                        : form.feedback.improvement_suggestions.every(({ active }) => !active)
                                }
                                kind={'copy'}
                                size={'small'}
                                tooltip={{ component: t('common:controls.copy', 'Copy') }}
                                variant={'square'}
                                onClick={handleClickCopyAll}
                            />
                            <IconButton
                                aria-label={'delete feedback'}
                                disabled={
                                    joyrideRun ||
                                    (isFeedbackRubric
                                        ? form.feedback.improvement_suggestions.every(
                                              ({ positive, negative }) => !positive.action && !negative.active
                                          )
                                        : form.feedback.improvement_suggestions.every(({ active }) => !active))
                                }
                                kind={'delete'}
                                size={'small'}
                                tooltip={{ component: t('common:controls.delete', 'Delete') }}
                                variant={'square'}
                                onClick={handleClickDeleteAll}
                            />
                        </Stack>
                    </BackgroundStack>
                    <Stack sx={{ position: 'relative', flex: 1, p: 3 }} spacing={2.5}>
                        {!form.feedback.improvement_suggestions.filter(({ active }) => active).length ? (
                            <Text
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                }}
                                color={'light'}
                            >
                                {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                            </Text>
                        ) : (
                            form.feedback.improvement_suggestions
                                .filter(({ active }) => active)
                                .map((item) =>
                                    isFeedbackRubric ? (
                                        <CriterionFeedback
                                            key={item.id}
                                            assignment={assignment}
                                            feedback={item}
                                            setForm={setForm}
                                            selectedFeedbackId={selectedFeedbackId}
                                            setSelectedFeedbackId={setSelectedFeedbackId}
                                            handleCriterionNavigation={handleCriterionNavigation}
                                            locationData={locationData}
                                            selectedFileId={selectedFileId}
                                            setSelectedFileId={setSelectedFileId}
                                        />
                                    ) : (
                                        <DetailedFeedback
                                            key={item.id}
                                            feedback={item}
                                            setForm={setForm}
                                            selectedFeedbackId={selectedFeedbackId}
                                            setSelectedFeedbackId={setSelectedFeedbackId}
                                        />
                                    )
                                )
                        )}
                    </Stack>
                </>
            )}
        </>
    );
};

const CriterionFeedback = ({
    assignment,
    feedback,
    setForm,
    selectedFeedbackId,
    setSelectedFeedbackId,
    handleCriterionNavigation,
    locationData,
    selectedFileId,
    setSelectedFileId,
}) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [isEditing, setIsEditing] = useState({ positive: false, negative: false });
    const [value, setValue] = useState({ positive: feedback.positive.text, negative: feedback.negative.text });
    const [isHovered, setIsHovered] = useState(false);

    const { locations, files } = locationData;

    const noFeedbackTranslationArgs = assignment?.api_assignment_id
        ? ['feedback-section.no-feedback-no-pushback', 'No feedback provided. This message will not be passed back.']
        : ['feedback-section.no-feedback', 'No feedback provided.'];

    const criterionIndex = assignment.rubric.criteria.findIndex(({ id }) => id === feedback.criterion_id);

    const handleSelect = async (key, targetItem, feedbackType) => {
        if (key === 'edit') {
            setIsEditing((current) => ({ ...current, [feedbackType]: true }));
            setValue((current) => ({ ...current, [feedbackType]: targetItem[feedbackType].text }));
        }
        if (key === 'copy') {
            await navigator.clipboard.writeText(stripHTML(targetItem[feedbackType].text));
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
                description: t(
                    'dialog.delete-feedback.description',
                    'Are you sure you want to delete this feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        feedback: {
                            ...current.feedback,
                            improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                return item.id === targetItem.id
                                    ? { ...item, [feedbackType]: { ...item[feedbackType], active: false } }
                                    : item;
                            }),
                        },
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickSave = (feedbackType) => {
        setForm((current) => {
            return {
                ...current,
                feedback: {
                    ...current.feedback,
                    improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                        return item.id === feedback.id
                            ? {
                                  ...item,
                                  [feedbackType]: {
                                      ...item[feedbackType],
                                      active: true,
                                      text: value[feedbackType],
                                  },
                              }
                            : item;
                    }),
                },
            };
        });
        setIsEditing((current) => ({ ...current, [feedbackType]: false }));
    };

    const handleClickCancel = (feedbackType) => {
        setIsEditing((current) => ({ ...current, [feedbackType]: false }));
        setValue((current) => ({ ...current, [feedbackType]: feedback[feedbackType].text }));
    };

    return (
        <CustomButtonBase
            id={`feedback-criterion-${criterionIndex}`}
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(selectedFeedbackId === feedback.id ? null : feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ mb: 1 }}>
                        <Text variant={'sh2'} weight={'semibold'}>
                            {assignment.rubric.criteria.find(({ id }) => id === feedback.criterion_id).title}
                        </Text>
                        <Button
                            variant={'secondary'}
                            onClick={() => handleCriterionNavigation(feedback.criterion_id)}
                            alignSelf={'flex-start'}
                        >
                            {t('rubric.view-rubric', 'View Rubric')}
                        </Button>
                    </Stack>
                    <Divider sx={{ mt: 1.5, mb: 2 }} />
                    <Stack spacing={3}>
                        <Stack sx={{ p: 1 }} spacing={2}>
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>{t('feedback-section.positive-title', 'What you did well')}</Text>
                                {!isEditing.positive && (
                                    <>
                                        {feedback.positive.active ? (
                                            <Dropdown
                                                id={`rubric-based-feedback-positive-${feedback.id}`}
                                                options={[
                                                    {
                                                        key: 'edit',
                                                        value: t('common:controls.edit', 'Edit'),
                                                        icon: 'edit',
                                                    },
                                                    {
                                                        key: 'copy',
                                                        value: t('common:controls.copy', 'Copy'),
                                                        icon: 'copy',
                                                    },
                                                    {
                                                        key: 'delete',
                                                        value: t('common:controls.delete', 'Delete'),
                                                        icon: 'delete',
                                                    },
                                                ]}
                                                rootComponent={
                                                    <IconButton
                                                        aria-label={'more options'}
                                                        kind={'moreHorizontal'}
                                                        size={'small'}
                                                        variant={'square'}
                                                    />
                                                }
                                                onSelect={({ key }) => handleSelect(key, feedback, 'positive')}
                                            />
                                        ) : (
                                            <IconButton
                                                kind={'add'}
                                                size={'small'}
                                                tooltip={{
                                                    component: t('feedback-section.add-feedback', 'Add Feedback'),
                                                }}
                                                variant={'square'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing((current) => ({ ...current, positive: true }));
                                                    setValue((current) => ({ ...current, positive: '' }));
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                            {isEditing.positive ? (
                                <Stack spacing={3}>
                                    <RichTextArea
                                        placeholder={t(
                                            'feedback-section.positive-placeholder',
                                            'Highlight what the student excelled at for this criterion.'
                                        )}
                                        value={value.positive}
                                        onChange={(html) => setValue((current) => ({ ...current, positive: html }))}
                                    />
                                    <Stack direction={'row-reverse'} spacing={3}>
                                        <Button onClick={() => handleClickSave('positive')}>
                                            {t('common:controls.save', 'Save')}
                                        </Button>
                                        <Button
                                            color={'neutral'}
                                            variant={'tertiary'}
                                            onClick={() => handleClickCancel('positive')}
                                        >
                                            {t('common:controls.cancel', 'Cancel')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <>
                                    {feedback.positive.active && feedback.positive.text ? (
                                        <ViewMore TextComponentProps={{ rich: true }}>
                                            {feedback.positive.text}
                                        </ViewMore>
                                    ) : feedback.positive.text === '' ? (
                                        <Text color={'light'}>{t(...noFeedbackTranslationArgs)}</Text>
                                    ) : (
                                        <Text color={'light'}>
                                            {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Stack>
                        <Stack
                            sx={(theme) => ({
                                borderRadius: '4px',
                                mx: -1,
                                p: 2,
                                background: theme.palette.ensightful.warning[200],
                            })}
                            spacing={2}
                        >
                            <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'}>
                                <Text variant={'p1'}>
                                    {t('feedback-section.improvement-title', 'How you can improve')}
                                </Text>
                                {!isEditing.negative && (
                                    <>
                                        {feedback.negative.active ? (
                                            <Dropdown
                                                id={`rubric-based-feedback-negative-${feedback.id}`}
                                                options={[
                                                    {
                                                        key: 'edit',
                                                        value: t('common:controls.edit', 'Edit'),
                                                        icon: 'edit',
                                                    },
                                                    {
                                                        key: 'copy',
                                                        value: t('common:controls.copy', 'Copy'),
                                                        icon: 'copy',
                                                    },
                                                    {
                                                        key: 'delete',
                                                        value: t('common:controls.delete', 'Delete'),
                                                        icon: 'delete',
                                                    },
                                                ]}
                                                rootComponent={
                                                    <IconButton
                                                        aria-label={'more options'}
                                                        kind={'moreHorizontal'}
                                                        size={'small'}
                                                        variant={'square'}
                                                    />
                                                }
                                                onSelect={({ key }) => handleSelect(key, feedback, 'negative')}
                                            />
                                        ) : (
                                            <IconButton
                                                kind={'add'}
                                                size={'small'}
                                                tooltip={{
                                                    component: t('feedback-section.add-feedback', 'Add Feedback'),
                                                }}
                                                variant={'square'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsEditing((current) => ({ ...current, negative: true }));
                                                    setValue((current) => ({ ...current, negative: '' }));
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </Stack>
                            {isEditing.negative ? (
                                <Stack spacing={3}>
                                    <RichTextArea
                                        placeholder={t(
                                            'feedback-section.improvement-placeholder',
                                            'Provide constructive feedback on areas where the student can improve for this criterion.'
                                        )}
                                        value={value.negative}
                                        onChange={(html) => setValue((current) => ({ ...current, negative: html }))}
                                    />
                                    <Stack direction={'row-reverse'} spacing={3}>
                                        <Button onClick={() => handleClickSave('negative')}>
                                            {t('common:controls.save', 'Save')}
                                        </Button>
                                        <Button
                                            color={'neutral'}
                                            variant={'tertiary'}
                                            onClick={() => handleClickCancel('negative')}
                                        >
                                            {t('common:controls.cancel', 'Cancel')}
                                        </Button>
                                    </Stack>
                                </Stack>
                            ) : (
                                <>
                                    {feedback.negative.active && feedback.negative.text ? (
                                        <ViewMore TextComponentProps={{ rich: true }}>
                                            {feedback.negative.text}
                                        </ViewMore>
                                    ) : feedback.negative.text === '' ? (
                                        <Text color={'light'}>{t(...noFeedbackTranslationArgs)}</Text>
                                    ) : (
                                        <Text color={'light'}>
                                            {t('feedback-section.deleted-feedback', 'This feedback was deleted.')}
                                        </Text>
                                    )}
                                </>
                            )}
                        </Stack>
                        {selectedFeedbackId === feedback.id && (
                            <Box sx={{ m: -3 }}>
                                <SupportingEvidence
                                    locations={locations}
                                    files={files}
                                    selectedFileId={selectedFileId}
                                    setSelectedFileId={setSelectedFileId}
                                />
                            </Box>
                        )}
                    </Stack>
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const DetailedFeedback = ({ feedback, setForm, selectedFeedbackId, setSelectedFeedbackId }) => {
    const { setDialog, closeDialog } = useDialog();
    const { joyrideRun } = useJoyride();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(feedback.text);
    const [isHovered, setIsHovered] = useState(false);

    const handleSelect = async (key, targetItem) => {
        if (key === 'edit') {
            setIsEditing(true);
            setValue(feedback.text);
        }
        if (key === 'copy') {
            await navigator.clipboard.writeText(stripHTML(targetItem.text));
            message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
        }
        if (key === 'delete') {
            setDialog({
                open: true,
                message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
                description: t(
                    'dialog.delete-feedback.description',
                    'Are you sure you want to delete this feedback? This can’t be undone.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.delete', 'Delete'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    setForm((current) => ({
                        ...current,
                        feedback: {
                            ...current.feedback,
                            improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                                return item.id === targetItem.id ? { ...item, active: false } : item;
                            }),
                        },
                    }));
                    closeDialog();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        }
    };

    const handleClickSave = () => {
        setForm((current) => ({
            ...current,
            feedback: {
                ...current.feedback,
                improvement_suggestions: current.feedback.improvement_suggestions.map((item) => {
                    return item.id === feedback.id
                        ? {
                              ...item,
                              text: value,
                          }
                        : item;
                }),
            },
        }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(feedback.text);
    };

    return isEditing ? (
        <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
            <Stack spacing={3}>
                <RichTextArea
                    placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                    value={value}
                    onChange={setValue}
                />
                <Stack direction={'row-reverse'} spacing={3}>
                    <Button onClick={handleClickSave}>{t('common:controls.save', 'Save')}</Button>
                    <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                </Stack>
            </Stack>
        </CustomPaper>
    ) : (
        <CustomButtonBase
            component={'div'}
            role={'presentation'}
            onClick={() => setSelectedFeedbackId(selectedFeedbackId === feedback.id ? null : feedback.id)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <CustomPaper isActive={selectedFeedbackId === feedback.id} variant={'outlined'}>
                <Stack alignItems={'flex-start'} direction={'row'} justifyContent={'space-between'} spacing={2}>
                    <Text align={'left'} rich>
                        {feedback.text}
                    </Text>
                    <Dropdown
                        id={`detailed-feedback-${feedback.id}`}
                        options={[
                            {
                                key: 'edit',
                                value: t('common:controls.edit', 'Edit'),
                                icon: 'edit',
                                disabled: joyrideRun,
                            },
                            { key: 'copy', value: t('common:controls.copy', 'Copy'), icon: 'copy' },
                            {
                                key: 'delete',
                                value: t('common:controls.delete', 'Delete'),
                                icon: 'delete',
                                disabled: joyrideRun,
                            },
                        ]}
                        rootComponent={
                            <IconButton
                                aria-label={'more options'}
                                kind={'moreHorizontal'}
                                size={'small'}
                                variant={'square'}
                            />
                        }
                        onSelect={({ key }) => handleSelect(key, feedback)}
                    />
                </Stack>
                {selectedFeedbackId !== feedback.id && isHovered && <LeftBorderHighlight />}
            </CustomPaper>
        </CustomButtonBase>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
        position: 'sticky',
        top: 0,
        zIndex: 1,
    };
});

const CenterText = styled(Text)(() => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '85%',
        transform: 'translate(-50%, -50%)',
    };
});

const CustomButtonBase = styled(ButtonBase)(({ theme }) => {
    return {
        scrollMarginTop: '52px',
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomPaper = styled(Paper)(({ theme, isActive }) => {
    return {
        position: 'relative',
        width: '100%',
        padding: theme.spacing(3),
        ...(isActive
            ? {
                  border: `2px solid ${theme.palette.ensightful.warning[700]}`,
              }
            : {
                  border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
                  padding: `calc(${theme.spacing(3)} + 1px)`,
              }),
    };
});

const LeftBorderHighlight = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '-1px',
        height: 'calc(100% - 28px)',
        width: '2px',
        background: theme.palette.ensightful.warning[700],
        transform: 'translateY(-50%)',
    };
});

export default FeedbackRenderer;
